import { Button } from "@/components/ui-ext/primitives";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

export function ChatMessageFeedback({
	content,
	feedback,
	disabled,
	onSubmit,
}: {
	content: string;
	feedback?: "helpful" | "not_helpful";
	disabled?: boolean;
	onSubmit: (verdict: "helpful" | "not_helpful", notes?: string) => Promise<void>;
}) {
	const [pendingNotHelpful, setPendingNotHelpful] = useState(false);
	const [feedbackNotes, setFeedbackNotes] = useState("");
	const [feedbackError, setFeedbackError] = useState<string | null>(null);
	const [submittingFeedback, setSubmittingFeedback] = useState(false);
	const [copied, setCopied] = useState(false);

	const copyMessage = async () => {
		const text = content.trim();
		if (!text) {
			return;
		}
		await navigator.clipboard.writeText(text);
		setCopied(true);
		window.setTimeout(() => setCopied(false), 1500);
	};

	return (
		<div className="mt-3 space-y-2">
			<div className="flex flex-wrap items-center gap-2">
				<span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
					Was this helpful?
				</span>
				<Button
					variant={feedback === "helpful" ? "primary" : "secondary"}
					size="sm"
					disabled={disabled || submittingFeedback || Boolean(feedback)}
					onClick={() => {
						void (async () => {
							setSubmittingFeedback(true);
							setFeedbackError(null);
							try {
								await onSubmit("helpful");
							} catch (error) {
								setFeedbackError(
									error instanceof Error ? error.message : "Failed to submit feedback.",
								);
							} finally {
								setSubmittingFeedback(false);
							}
						})();
					}}
				>
					Helpful
				</Button>
				<Button
					variant={feedback === "not_helpful" ? "primary" : "secondary"}
					size="sm"
					disabled={disabled || submittingFeedback || Boolean(feedback)}
					onClick={() => {
						setFeedbackError(null);
						setPendingNotHelpful(true);
					}}
				>
					Not helpful
				</Button>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-8 w-8 shrink-0 justify-center px-0"
					aria-label={copied ? "Copied" : "Copy message"}
					title={copied ? "Copied" : "Copy message"}
					disabled={!content.trim()}
					onClick={() => void copyMessage()}
				>
					{copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
				</Button>
			</div>
			{feedbackError && !pendingNotHelpful && (
				<p className="text-xs text-[var(--danger)]">{feedbackError}</p>
			)}
			{pendingNotHelpful && !feedback && (
				<div className="rounded-[10px] border border-soft bg-background p-3">
					<label className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
						What was wrong or missing?
					</label>
					<textarea
						value={feedbackNotes}
						onChange={(event) => setFeedbackNotes(event.target.value)}
						rows={3}
						className="mt-1 w-full rounded-[10px] border border-soft bg-background px-3 py-2 text-xs text-anchor outline-none focus:border-[var(--primary)]"
						placeholder="e.g. Wrong courses, missing duration, answer not grounded in sources..."
					/>
					{feedbackError && <p className="mt-1 text-xs text-[var(--danger)]">{feedbackError}</p>}
					<div className="mt-2 flex flex-wrap gap-2">
						<Button
							size="sm"
							disabled={disabled || submittingFeedback}
							onClick={() => {
								void (async () => {
									const notes = feedbackNotes.trim();
									if (notes.length < 10) {
										setFeedbackError(
											"Please add at least 10 characters so we can improve chat quality.",
										);
										return;
									}
									setSubmittingFeedback(true);
									setFeedbackError(null);
									try {
										await onSubmit("not_helpful", notes);
										setPendingNotHelpful(false);
										setFeedbackNotes("");
									} catch (error) {
										setFeedbackError(
											error instanceof Error ? error.message : "Failed to submit feedback.",
										);
									} finally {
										setSubmittingFeedback(false);
									}
								})();
							}}
						>
							{submittingFeedback ? "Sending..." : "Submit feedback"}
						</Button>
						<Button
							size="sm"
							variant="secondary"
							disabled={submittingFeedback}
							onClick={() => {
								setPendingNotHelpful(false);
								setFeedbackError(null);
							}}
						>
							Cancel
						</Button>
					</div>
				</div>
			)}
		</div>
	);
}
